/**
 * Sichtbarkeits-Vererbung im Storage (rein, ohne UI-Abhängigkeiten).
 *
 * Spiegelt die Backend-Logik aus `storage/views.py`
 * (`_intersect_audience` / `_folder_visible_including_ancestors`): Die effektive
 * Zielgruppe eines Unterordners ist die Schnittmenge seiner eigenen Zielgruppe
 * mit der aller Vorfahren. Das Backend bleibt maßgeblich für die Durchsetzung –
 * diese Helfer dienen nur der verständlichen Anzeige/Vorschau/Warnung.
 */

export type AudienceMode = 'DEFAULT_INTERNAL' | 'ALL_USERS' | 'RESTRICTED' | 'NONE';

export interface Audience {
    mode: AudienceMode;
    /** Nur bei RESTRICTED befüllt (aufsteigend, dedupliziert). */
    groupIds: number[];
}

const sortedUnique = (ids: number[]): number[] => Array.from(new Set(ids)).sort((a, b) => a - b);

/** Normalisiert (Modus, Gruppen) zu einer Audience. */
export const audienceFromMode = (mode: AudienceMode, groupIds: number[] = []): Audience => {
    if (mode === 'RESTRICTED') {
        return { mode: 'RESTRICTED', groupIds: sortedUnique(groupIds) };
    }
    return { mode, groupIds: [] };
};

/**
 * Schnittmenge zweier Zielgruppen (wer beide sehen darf). Reihenfolge egal.
 * ALL_USERS ⊇ DEFAULT_INTERNAL ⊇ RESTRICTED(interne Gruppen 1-3).
 */
export const intersectAudience = (a: Audience, b: Audience): Audience => {
    if (a.mode === 'NONE' || b.mode === 'NONE') {
        return { mode: 'NONE', groupIds: [] };
    }
    if (a.mode === 'ALL_USERS') {
        return { mode: b.mode, groupIds: [...b.groupIds] };
    }
    if (b.mode === 'ALL_USERS') {
        return { mode: a.mode, groupIds: [...a.groupIds] };
    }
    if (a.mode === 'DEFAULT_INTERNAL' && b.mode === 'DEFAULT_INTERNAL') {
        return { mode: 'DEFAULT_INTERNAL', groupIds: [] };
    }
    if (a.mode === 'DEFAULT_INTERNAL') {
        return { mode: b.mode, groupIds: [...b.groupIds] };
    }
    if (b.mode === 'DEFAULT_INTERNAL') {
        return { mode: a.mode, groupIds: [...a.groupIds] };
    }
    // Beide RESTRICTED.
    const common = a.groupIds.filter((id) => b.groupIds.includes(id));
    if (common.length === 0) {
        return { mode: 'NONE', groupIds: [] };
    }
    return { mode: 'RESTRICTED', groupIds: sortedUnique(common) };
};

export const audiencesEqual = (a: Audience, b: Audience): boolean => {
    if (a.mode !== b.mode) {
        return false;
    }
    if (a.groupIds.length !== b.groupIds.length) {
        return false;
    }
    return a.groupIds.every((id, index) => id === b.groupIds[index]);
};

/**
 * Schränkt der Elternordner die gewählte direkte Sichtbarkeit weiter ein?
 * (Die effektive Sichtbarkeit weicht dann von der direkten Wahl ab.)
 */
export const isRestrictedByParent = (childDirect: Audience, parentEffective: Audience | null): boolean => {
    if (parentEffective === null) {
        return false;
    }
    return !audiencesEqual(intersectAudience(parentEffective, childDirect), childDirect);
};

/** Minimaler Ordner-Ausschnitt für die Ableitung der Sichtbarkeits-Audience. */
export interface FolderVisibilitySource {
    visibility_mode?: string;
    include_guests?: boolean;
    visible_groups?: number[];
    visibility_groups?: number[];
    effective_visibility_mode?: string;
    effective_visibility_group_ids?: number[];
}

/** Backend-Modus (inkl. Legacy-Werte/NONE) auf die vier Audience-Modi abbilden. */
export const toAudienceMode = (
    mode: string | undefined,
    groupIds: number[] | undefined,
    includeGuests: boolean | undefined,
): AudienceMode => {
    if (mode === 'NONE') {
        return 'NONE';
    }
    if (mode === 'ALL_USERS' || includeGuests) {
        return 'ALL_USERS';
    }
    if (mode === 'RESTRICTED' || mode === 'GROUPS_ONLY' || (groupIds?.length ?? 0) > 0) {
        return 'RESTRICTED';
    }
    return 'DEFAULT_INTERNAL';
};

/** Direkt am Ordner eingestellte Sichtbarkeit. */
export const directAudienceFromNode = (node: FolderVisibilitySource): Audience => {
    const groupIds = node.visibility_groups ?? node.visible_groups ?? [];
    return audienceFromMode(toAudienceMode(node.visibility_mode, groupIds, node.include_guests), groupIds);
};

/** Effektive Sichtbarkeit nach Vererbung (Fallback: direkte, falls Backend älter). */
export const effectiveAudienceFromNode = (node: FolderVisibilitySource): Audience => {
    if (node.effective_visibility_mode) {
        const groupIds = node.effective_visibility_group_ids ?? [];
        return audienceFromMode(toAudienceMode(node.effective_visibility_mode, groupIds, undefined), groupIds);
    }
    return directAudienceFromNode(node);
};

/** Menschenlesbares Label; Gruppen-IDs werden über resolveGroupName aufgelöst. */
export const describeAudience = (
    audience: Audience,
    resolveGroupName: (groupId: number) => string,
): string => {
    switch (audience.mode) {
        case 'ALL_USERS':
            return 'Alle angemeldeten Nutzer (inkl. Gäste)';
        case 'DEFAULT_INTERNAL':
            return 'Alle Mitglieder (außer Gäste)';
        case 'NONE':
            return 'Niemand (durch übergeordnete Ordner vollständig eingeschränkt)';
        case 'RESTRICTED':
        default:
            if (audience.groupIds.length === 0) {
                return 'Nur bestimmte Gruppen';
            }
            return `Nur: ${audience.groupIds.map(resolveGroupName).join(', ')}`;
    }
};
